import {AxiosInstance, AxiosResponse} from 'axios';
import {SILENT_ERROR_HEADER} from '../../api/axios';
import { TimeTrackingImportPreviewDto, TimeTrackingImportSummaryDto } from './types';

export interface ITimeTrackingImportApi {
    preview(data: FormData): Promise<AxiosResponse<TimeTrackingImportPreviewDto>>;
    runImport(data: FormData): Promise<AxiosResponse<TimeTrackingImportSummaryDto>>;
    downloadTemplate(year?: number, month?: number): Promise<AxiosResponse<Blob>>;
}

export default class TimeTrackingImportApi implements ITimeTrackingImportApi {
    private readonly _axiosInstance: AxiosInstance;

    constructor(axiosInstance: AxiosInstance) {
        this._axiosInstance = axiosInstance;
    }

    async preview(data: FormData): Promise<AxiosResponse<TimeTrackingImportPreviewDto>> {
        return await this._axiosInstance.post<TimeTrackingImportPreviewDto>(`${this.getController()}/preview`, data);
    }

    async runImport(data: FormData): Promise<AxiosResponse<TimeTrackingImportSummaryDto>> {
        return await this._axiosInstance.post<TimeTrackingImportSummaryDto>(`${this.getController()}/import`, data);
    }

    async downloadTemplate(year?: number, month?: number): Promise<AxiosResponse<Blob>> {
        const params = [
            year !== undefined ? `year=${year}` : '',
            month !== undefined ? `month=${month}` : '',
        ].filter(Boolean).join('&');
        const query = params ? `?${params}` : '';

        // The error body of a blob request is itself a Blob, so the global interceptor can only
        // surface a bare status text — the caller reports a proper message instead.
        return await this._axiosInstance.get<Blob>(`${this.getController()}/template${query}`, {
            responseType: 'blob',
            headers: {[SILENT_ERROR_HEADER]: 'true'},
        });
    }

    private getController(): string {
        const baseUrl = this._axiosInstance.defaults.baseURL ?? '';
        const normalizedBaseUrl = baseUrl.toLowerCase().replace(/\/+$/, '');

        if (normalizedBaseUrl.endsWith('/api/v2')) {
            return 'time-tracking-import';
        }

        if (normalizedBaseUrl.endsWith('/api')) {
            return 'v2/time-tracking-import';
        }

        return 'api/v2/time-tracking-import';
    }
}
